

import React from 'react';
import bestpractice from '../../../assets/image/bestpractice.svg';

interface AttendanceGuidelineModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const AttendanceGuidelineModal: React.FC<AttendanceGuidelineModalProps> = ({ isOpen, onClose }) => {
    if (!isOpen) return null;

    return (
        <div 
            className="modal fade show d-block" 
            tabIndex={-1} 
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.4)', zIndex: 1055 }}
            onClick={onClose}
        >
            <div 
                className="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable" 
                onClick={(e) => e.stopPropagation()}
            >
                <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '8px' }}>
                    
                    <div className="modal-header border-bottom px-5 py-4 d-flex align-items-center justify-content-between">
                        <div>
                            <h5 className="fw-bold text-dark mb-0">Attendance Status Configuration Guidelines</h5>
                            <p className="text-muted fs-8 mb-0 mt-1">Configure attendance rules from top to bottom. The system evaluates each rule in the displayed order and stops at the first condition that matches.</p>
                            <p className="text-muted fs-8 mb-0 mt-1">A broader condition placed above a more specific one can prevent the intended status from ever being applied.</p>
                        </div>
                        <button 
                            type="button" 
                            className="btn-close fs-7" 
                            aria-label="Close" 
                            onClick={onClose}
                        ></button>
                    </div>

                    <div className="modal-body px-5 py-4">
                        
                        <div className="mb-5">
                            <h6 className="fw-bold fs-7 mb-2 d-flex align-items-center gap-1 text-dark">
                                <i className="bi bi-check-circle-fill fs-6 text-success"></i> Correct Example
                            </h6>

                            <table className="table table-bordered align-middle text-center mb-2 fs-7">
                                <thead>
                                    <tr className="text-gray-600 fw-semibold fs-7 bg-light">
                                        <th style={{ width: '15%' }} className="py-2">Order</th>
                                        <th style={{ width: '30%' }} className="py-2">Status</th>
                                        <th style={{ width: '55%' }} className="py-2">Condition</th>
                                    </tr>
                                </thead>
                                <tbody className="text-gray-700">
                                    <tr>
                                        <td className="fw-medium text-gray-600">1</td>
                                        <td>
                                            <span className="badge bg-success-subtle text-success border border-success-subtle rounded-pill px-3 py-1 fs-8 fw-semibold">
                                                ● Present
                                            </span>
                                        </td>
                                        <td className="fw-medium text-gray-800">Hours Worked ≥ 7</td>
                                    </tr>
                                    <tr>
                                        <td className="fw-medium text-gray-600">2</td>
                                        <td>
                                            <span className="badge bg-primary-subtle text-primary border border-primary-subtle rounded-pill px-3 py-1 fs-8 fw-semibold">
                                                ● Half Day
                                            </span>
                                        </td>
                                        <td className="fw-medium text-gray-800">Hours Worked ≥ 5</td>
                                    </tr>
                                    <tr>
                                        <td className="fw-medium text-gray-600">3</td>
                                        <td>
                                            <span className="badge bg-warning-subtle text-warning border border-warning-subtle rounded-pill px-3 py-1 fs-8 fw-semibold">
                                                ● Short Day
                                            </span>
                                        </td>
                                        <td className="fw-medium text-gray-800">Hours Worked &gt; 0</td>
                                    </tr>
                                    <tr>
                                        <td className="fw-medium text-gray-600">4</td>
                                        <td>
                                            <span className="badge bg-danger-subtle text-danger border border-danger-subtle rounded-pill px-3 py-1 fs-8 fw-semibold">
                                                ● Absent
                                            </span>
                                        </td>
                                        <td className="fw-medium text-gray-800">Hours Worked = 0</td>
                                    </tr>
                                </tbody>
                            </table>

                            <div className="text-muted fs-7">
                                <span className="fw-semibold text-gray-700">How this works:</span>
                                <br />
                                8 hours → Present | 6 hours → Half Day | 2 hours → Short Day | 0 hours → Absent.
                                <br />
                                <span className="text-gray-600">Each condition is more specific than the one below it, ensuring every employee receives the correct attendance status.</span>
                            </div>
                        </div>

                        <div className="mb-5">
                            <h6 className="fw-bold fs-7 mb-2 d-flex align-items-center gap-1 text-dark">
                                <i className="bi bi-x-circle-fill fs-6 text-danger"></i> Incorrect Example
                            </h6>

                            <table className="table table-bordered align-middle text-center mb-2 fs-7">
                                <thead>
                                    <tr className="text-gray-600 fw-semibold fs-7 bg-light">
                                        <th style={{ width: '15%' }} className="py-2">Order</th>
                                        <th style={{ width: '30%' }} className="py-2">Status</th>
                                        <th style={{ width: '55%' }} className="py-2">Condition</th>
                                    </tr>
                                </thead>
                                <tbody className="text-gray-700">
                                    <tr>
                                        <td className="fw-medium text-gray-600">1</td>
                                        <td>
                                            <span className="badge bg-warning-subtle text-warning border border-warning-subtle rounded-pill px-3 py-1 fs-8 fw-semibold">
                                                ● Short Day
                                            </span>
                                        </td>
                                        <td className="fw-medium text-gray-800">Hours Worked &gt; 0</td>
                                    </tr>
                                    <tr>
                                        <td className="fw-medium text-gray-600">2</td>
                                        <td>
                                            <span className="badge bg-primary-subtle text-primary border border-primary-subtle rounded-pill px-3 py-1 fs-8 fw-semibold">
                                                ● Half Day
                                            </span>
                                        </td>
                                        <td className="fw-medium text-gray-800">Hours Worked ≥ 5</td>
                                    </tr>
                                    <tr>
                                        <td className="fw-medium text-gray-600">3</td>
                                        <td>
                                            <span className="badge bg-success-subtle text-success border border-success-subtle rounded-pill px-3 py-1 fs-8 fw-semibold">
                                                ● Present
                                            </span>
                                        </td>
                                        <td className="fw-medium text-gray-800">Hours Worked ≥ 7</td>
                                    </tr>
                                    <tr>
                                        <td className="fw-medium text-gray-600">4</td>
                                        <td>
                                            <span className="badge bg-danger-subtle text-danger border border-danger-subtle rounded-pill px-3 py-1 fs-8 fw-semibold">
                                                ● Absent
                                            </span>
                                        </td>
                                        <td className="fw-medium text-gray-800">Hours Worked = 0</td>
                                    </tr>
                                </tbody>
                            </table>

                            <div className="text-muted fs-7">
                                <span className="fw-semibold">What goes wrong?</span>
                                <br />
                                Since <strong>"Hours Worked &gt; 0"</strong> matches anyone who has worked even a small amount of time, employees who worked 5, 7, or even 10 hours will immediately be marked as <strong>Short Day</strong>.
                                <br />
                                 <br />
                                <span className="fw-medium text-gray-700">As a result:</span>
                                <ul className="list-unstyled mb-0 ms-2">
                                    <li><i className="fas fa-times text-danger"></i> <strong>Half Day</strong> will never be assigned.</li>
                                    <li><i className="fas fa-times text-danger"></i> <strong>Present</strong> will never be assigned.</li>
                                </ul>
                            </div>
                        </div>

                        <div className="pt-2">
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <i className="far fa-lightbulb text-warning fs-6"></i>
                                <h6 className="fw-bold text-gray-800 fs-7 mb-0">Best Practices</h6>
                            </div>
                            <ul className="mb-0 ps-4 text-gray-700 fs-8 d-flex flex-column gap-1">
                                <li>Arrange rules from <strong>most specific to least specific</strong>.</li>
                                <li>Place higher thresholds before lower thresholds.</li>
                                <li>Keep <strong>Absent (Hours Worked = 0)</strong> as the last rule.</li>
                                <li>Avoid creating overlapping rules unless the desired priority is reflected in the ordering.</li>
                                <li>After making changes, review the rule order to ensure the first matching condition always produces the expected attendance status.</li>
                            </ul>
                        </div>

                    </div>

                    <div className="modal-footer border-top px-5 py-3">
                        <button 
                            type="button" 
                            className="btn btn-sm btn-secondary fs-7 px-4 text-gray-700"
                            onClick={onClose}
                        >
                            Close
                        </button>
                    </div>

                </div>
            </div>
        </div>
    );
};

export default AttendanceGuidelineModal;